
import React, { useEffect, lazy, Suspense } from "react";
import { captureAvmAttribution } from "@/lib/avmTracking";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';
import { AuthProvider } from "./contexts/auth/AuthProvider";
import { CartProvider } from "@/contexts/cart/CartProvider";
import { PerformanceProfiler } from "./components/PerformanceProfiler";
import TikTokPixel from "./components/TikTokPixel";
import { PageTracker } from "./components/PageTracker";
import { useShopifyCartSync } from "@/hooks/useShopifyCartSync";

const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
import { supabase } from '@/integrations/supabase/client';

// Critical page - only Index loaded immediately for LCP
import Index from "./pages/Index";
import PaymentSuccess from "./pages/PaymentSuccess";

// Lazy load all non-homepage pages
const Products = lazy(() => import("./pages/Products"));
const Cart = lazy(() => import("./pages/Cart"));

// Shopify storefront pages
const ShopifyHome = lazy(() => import("./pages/ShopifyHome"));
const ShopifyProducts = lazy(() => import("./pages/ShopifyProducts"));
const ShopifyProductDetail = lazy(() => import("./pages/ShopifyProductDetail"));

// Lazy load non-critical pages for better initial load
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Shipping = lazy(() => import("./pages/Shipping"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const Payment = lazy(() => import("./pages/Payment"));

const Profile = lazy(() => import("./pages/Profile"));
const Reviews = lazy(() => import("./pages/Reviews"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const BehindBottle = lazy(() => import('./pages/BehindBottle'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages - lazy load as they're rarely accessed
const Admin = lazy(() => import("./pages/Admin"));
const AdminPromoCode = lazy(() => import("./pages/AdminPromoCode"));
const AdminSubscribers = lazy(() => import("./pages/AdminSubscribers"));
const DataExport = lazy(() => import("./pages/DataExport"));
const AdminTaggbox = lazy(() => import('./pages/AdminTaggbox'));
const AdminInstagram = lazy(() => import('./pages/AdminInstagram'));
const SocialFeedDemo = lazy(() => import('./pages/SocialFeedDemo'));
const AdminStockManagement = lazy(() => import('./pages/AdminStockManagement'));

// Page loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-stone-100">
    <div className="w-10 h-10 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin"></div>
  </div>
);

// Optimized QueryClient with caching strategies
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data fresh for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Cache data for 30 minutes
      gcTime: 1000 * 60 * 30,
      // Disable automatic refetching for better performance
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Retry failed requests only once
      retry: 1,
      retryDelay: 1000,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Test Supabase connection at startup (removed console logs for performance)

function ShopifyCartSyncWrapper() {
  useShopifyCartSync();
  return null;
}

function App() {
  // Capture AvantLink affiliate referral (?avad=...) on any landing page
  useEffect(() => {
    captureAvmAttribution();
  }, []);

  // Handle password reset redirects
  useEffect(() => {
    const handleAuthRedirect = () => {
      const currentPath = window.location.pathname;
      
      // Check for recovery type in both query params and hash
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const searchParams = new URLSearchParams(window.location.search);
      
      const type = searchParams.get('type') || hashParams.get('type');
      const accessToken = searchParams.get('access_token') || hashParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token') || hashParams.get('refresh_token');
      
      // If we have recovery tokens and we're not already on the update-password page
      if (type === 'recovery' && accessToken && refreshToken && currentPath !== '/update-password') {
        // Preserve all the auth tokens in the URL when redirecting
        const newUrl = `/update-password${window.location.search}${window.location.hash}`;
        window.location.replace(newUrl);
      }
    };
    
    // Run immediately and also on hash change
    handleAuthRedirect();
    
    // Listen for hash changes (in case user navigates back/forward)
    const handleHashChange = () => {
      handleAuthRedirect();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
  
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <HelmetProvider>
          <BrowserRouter>
            <TooltipProvider>
              <PerformanceProfiler id="AuthProvider" logThreshold={50}>
                <AuthProvider>
                  <PerformanceProfiler id="CartProvider" logThreshold={50}>
                    <CartProvider>
                      <TikTokPixel />
                      <PageTracker />
                      <ShopifyCartSyncWrapper />
                      <Suspense fallback={null}><Toaster /></Suspense>
                      <PerformanceProfiler id="Routes" logThreshold={100}>
                        <Routes>
                          {/* Critical routes - no Suspense needed */}
                          <Route path="/" element={<Index />} />
                          <Route path="/cart" element={<Suspense fallback={<PageLoader />}><Cart /></Suspense>} />
                          <Route path="/product/:id" element={<Suspense fallback={<PageLoader />}><ProductDetail /></Suspense>} />
                          <Route path="/products" element={<Suspense fallback={<PageLoader />}><Products /></Suspense>} />
                          
                          {/* Shopify storefront routes */}
                          <Route path="/shopify" element={<Suspense fallback={<PageLoader />}><ShopifyHome /></Suspense>} />
                          <Route path="/shopify/products" element={<Suspense fallback={<PageLoader />}><ShopifyProducts /></Suspense>} />

                          <Route path="/shopify/product/:handle" element={<Suspense fallback={<PageLoader />}><ShopifyProductDetail /></Suspense>} />
                          
                          {/* Non-critical routes - with Suspense */}
                          <Route path="/shipping" element={<Suspense fallback={<PageLoader />}><Shipping /></Suspense>} />
                          <Route path="/payment" element={<Suspense fallback={<PageLoader />}><Payment /></Suspense>} />
                          <Route path="/payment-success" element={<PaymentSuccess />} />
                          <Route path="/login" element={<Suspense fallback={<PageLoader />}><Login /></Suspense>} />
                          <Route path="/register" element={<Suspense fallback={<PageLoader />}><Register /></Suspense>} />
                          <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
                          <Route path="/auth/callback" element={<Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>} />
                          <Route path="/update-password" element={<Suspense fallback={<PageLoader />}><UpdatePassword /></Suspense>} />
                          <Route path="/profile" element={<Suspense fallback={<PageLoader />}><Profile /></Suspense>} />
                          <Route path="/behind-bottle" element={<Suspense fallback={<PageLoader />}><BehindBottle /></Suspense>} />
                          <Route path="/reviews" element={<Suspense fallback={<PageLoader />}><Reviews /></Suspense>} />
                          <Route path="/faq" element={<Suspense fallback={<PageLoader />}><FAQ /></Suspense>} />
                          <Route path="/contact" element={<Suspense fallback={<PageLoader />}><Contact /></Suspense>} />
                          <Route path="/shipping-policy" element={<Suspense fallback={<PageLoader />}><ShippingPolicy /></Suspense>} />
                          <Route path="/privacy-policy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
                          <Route path="/terms-of-service" element={<Suspense fallback={<PageLoader />}><TermsOfService /></Suspense>} />
                          
                          {/* Admin routes - all lazy loaded */}
                          <Route path="/admin" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/promo-codes" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/subscribers" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/credit" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/users" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/product-prices" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/orders" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/tax" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/promotions" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/analytics" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/commissions" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
                          <Route path="/admin/promo-codes/standalone" element={<Suspense fallback={<PageLoader />}><AdminPromoCode /></Suspense>} />
                          <Route path="/admin/subscribers/standalone" element={<Suspense fallback={<PageLoader />}><AdminSubscribers /></Suspense>} />
                          <Route path="/export" element={<Suspense fallback={<PageLoader />}><DataExport /></Suspense>} />
                          <Route path="/admin/taggbox" element={<Suspense fallback={<PageLoader />}><AdminTaggbox /></Suspense>} />
                          <Route path="/admin/instagram" element={<Suspense fallback={<PageLoader />}><AdminInstagram /></Suspense>} />
                          <Route path="/admin/social-demo" element={<Suspense fallback={<PageLoader />}><SocialFeedDemo /></Suspense>} />
                          <Route path="/admin/stock" element={<Suspense fallback={<PageLoader />}><AdminStockManagement /></Suspense>} />
                          
                          <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
                        </Routes>
                      </PerformanceProfiler>
                    </CartProvider>
                  </PerformanceProfiler>
                </AuthProvider>
              </PerformanceProfiler>
            </TooltipProvider>
          </BrowserRouter>
        </HelmetProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
